import React, { useState } from "react";
import {
  StyleSheet,
  ActivityIndicator,
  View,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  useWindowDimensions,
} from "react-native";
import { Redirect, useRouter } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import useAuth from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { Image, ImageSource } from "expo-image";
import { MaterialIcons } from "@expo/vector-icons";
import Head from "expo-router/head";

export default function SignIn() {
  const router = useRouter();
  const { colors } = useTheme();
  const { session, signIn, loading, lastUsedEmail } = useAuth();
  const { width } = useWindowDimensions();

  const [email, setEmail] = useState(lastUsedEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [logo] = useState<ImageSource>(require("@/assets/images/rockforce-logo-rf-yellow.svg"));
  const showLogo = width <= 900;

  const handleSignIn = async () => {
    if (!email || !password) return;
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
      router.navigate("/");
    } catch (error: any) {
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (session && !loading) {
    return <Redirect href="/" />;
  }

  return (
    <>
      <Head>
        <title>RockForce Sign-in</title>
        <meta name="description" content="" />
      </Head>
      <View style={styles.outerContainer}>
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          keyboardVerticalOffset={Platform.OS === "ios" ? 150 : 0}
          style={styles.keyboardView}
        >
          {/* Logo Section */}
          {showLogo && (
            <View style={styles.logoSection}>
              <Image source={[logo]} style={styles.logo} />
              <Image source={require("@/assets/images/rockforce-logo-white.svg")} style={{ height: 28, width: 200 }} />
              <ThemedText style={[styles.logoSubtitle, { color: colors.textSecondary }]}>
                Payroll • Staffing • Ops
              </ThemedText>
            </View>
          )}

          {/* Sign In Card */}
          <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <ThemedText style={[styles.cardTitle, { color: colors.text }]}>Welcome Back</ThemedText>
            <ThemedText style={[styles.cardSubtitle, { color: colors.textSecondary }]}>
              Sign in to your account
            </ThemedText>

            {/* Form */}
            <View style={styles.form}>
              <View style={styles.inputContainer}>
                <Input
                  id="email-input"
                  icon="email"
                  placeholder="Email"
                  value={email}
                  onChangeText={setEmail}
                  autoComplete="email"
                  containerStyle={styles.input}
                  autoFocus={false}
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
              </View>

              <View style={styles.inputContainer}>
                <Input
                  id="password-input"
                  icon="lock"
                  value={password}
                  onChangeText={setPassword}
                  onSubmitEditing={handleSignIn}
                  placeholder="Password"
                  containerStyle={styles.input}
                  autoComplete="current-password"
                  secureTextEntry={!showPassword}
                />
                <Pressable style={styles.eyeButton} onPress={() => setShowPassword(!showPassword)}>
                  <MaterialIcons
                    name={showPassword ? "visibility" : "visibility-off"}
                    size={20}
                    color={colors.textSecondary}
                  />
                </Pressable>
              </View>

              {/* Error Message */}
              {error && (
                <View style={[styles.errorCard, { backgroundColor: colors.ruby?.background || "#fee2e2" }]}>
                  <MaterialIcons name="error" size={18} color={colors.ruby?.text || "#ef4444"} />
                  <ThemedText style={[styles.errorText, { color: colors.ruby?.text || "#ef4444" }]}>{error}</ThemedText>
                </View>
              )}

              {/* Sign In Button */}
              {loading || submitting ? (
                <ActivityIndicator size="large" color={colors.primary} style={styles.loader} />
              ) : (
                <Button
                  id="sign-in"
                  text="Sign In"
                  onPress={handleSignIn}
                  icon="login"
                  iconPosition="right"
                  iconSize={20}
                  iconColor="white"
                  enabled={!!(email && password)}
                  style={[
                    styles.signInButton,
                    {
                      backgroundColor: email && password ? colors.primary : colors.border,
                      opacity: email && password ? 1 : 0.6,
                    },
                  ]}
                  textStyle={styles.signInButtonText}
                />
              )}
            </View>

            {/* Forgot Password Link */}
            <Pressable
              style={[styles.forgotPasswordButton, { borderColor: colors.border }]}
              onPress={() => router.push("/forgot-password")}
            >
              <MaterialIcons name="help-outline" size={18} color={colors.primary} />
              <ThemedText style={[styles.forgotPasswordText, { color: colors.primary }]}>Forgot Password?</ThemedText>
            </Pressable>
          </View>

          {/* Apply Section */}
          <View style={[styles.registerCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <ThemedText style={[styles.registerText, { color: colors.textSecondary }]}>
              Interested in joining the team?
            </ThemedText>
            <Button
              text="Apply Now"
              id="apply"
              onPress={() => router.push("/apply")}
              icon="file-document-edit-outline"
              iconPosition="right"
              iconSize={18}
              iconColor={colors.primary}
              style={[styles.registerButton, { borderColor: colors.primary }]}
              textStyle={[styles.registerButtonText, { color: colors.primary }]}
            />
          </View>
        </KeyboardAvoidingView>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    alignItems: "center",
  },
  keyboardView: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    width: "100%",
    maxWidth: 400,
  },
  logoSection: {
    alignItems: "center",
    marginBottom: 32,
  },
  logo: {
    height: 64,
    width: 64,
    marginBottom: 12,
  },
  logoTitle: {
    fontSize: 28,
    fontWeight: "800",
    letterSpacing: 2,
  },
  logoSubtitle: {
    fontSize: 14,
    marginTop: 4,
  },
  card: {
    width: "100%",
    borderRadius: 16,
    borderWidth: 1,
    padding: 24,
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 22,
    fontWeight: "bold",
    marginBottom: 4,
  },
  cardSubtitle: {
    fontSize: 14,
    marginBottom: 24,
  },
  form: {
    gap: 16,
  },
  inputContainer: {
    position: "relative",
  },
  input: {
    minWidth: "100%",
  },
  eyeButton: {
    position: "absolute",
    right: 12,
    top: 0,
    bottom: 0,
    justifyContent: "center",
    padding: 8,
  },
  errorCard: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 12,
    borderRadius: 8,
  },
  errorText: {
    flex: 1,
    fontSize: 14,
  },
  loader: {
    marginVertical: 8,
  },
  signInButton: {
    borderRadius: 10,
    minHeight: 50,
    marginTop: 8,
  },
  signInButtonText: {
    color: "white",
    fontSize: 17,
    fontWeight: "600",
  },
  forgotPasswordButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 20,
    paddingVertical: 12,
    borderTopWidth: 1,
  },
  forgotPasswordText: {
    fontSize: 15,
    fontWeight: "500",
  },
  registerCard: {
    width: "100%",
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    alignItems: "center",
    gap: 12,
  },
  registerText: {
    fontSize: 14,
  },
  registerButton: {
    borderWidth: 1.5,
    borderRadius: 10,
    minHeight: 44,
    backgroundColor: "transparent",
    width: "100%",
  },
  registerButtonText: {
    fontSize: 15,
    fontWeight: "600",
  },
});
